import { applyToPoint, computeItemLayout, groupContentMatrix } from '../core/geometry';
import type { SceneItem, Vec2 } from '../core/model';
import type { ProgramSourceContribution } from './api';
export interface ConsumedNode {
  item: SceneItem; natW: number; natH: number; drawable: boolean; src: unknown;
  sprite: { visible: boolean }; mesh: { visible: boolean } | null;
  children: ConsumedNode[] | null; groupW: number; groupH: number; groupMinX: number; groupMinY: number;
}
type Contribution = { source_id: string; kind: string; polygon: Vec2[] };
const MAX_ITEMS = 512, MAX_VERTICES = 16, MAX_FRAGMENTS = 512, MAX_OPERATIONS = 4096, MAX_DEPTH = 8;
export function clippedPolygon(points: Vec2[], x: number, y: number, width: number, height: number): Vec2[] {
  if (![x, y, width, height, ...points.flatMap((p) => [p.x, p.y])].every(Number.isFinite) || width <= 0 || height <= 0) return [];
  for (const [axis, bound, lower] of [['x', x, true], ['x', x + width, false], ['y', y, true], ['y', y + height, false]] as const) {
    const next: Vec2[] = [], inside = (p: Vec2) => lower ? p[axis] >= bound : p[axis] <= bound;
    for (let i = 0; i < points.length; i++) {
      const p = points[i]!, q = points[(i + 1) % points.length]!;
      if (inside(p)) next.push(p);
      if (inside(p) !== inside(q)) { const t = (bound - p[axis]) / (q[axis] - p[axis]); next.push({ x: p.x + (q.x - p.x) * t, y: p.y + (q.y - p.y) * t }); }
    }
    points = next; if (points.length < 3) return [];
  }
  return points;
}
export const polygonArea = (p: Vec2[]) => Math.abs(p.reduce((sum, a, i) => { const b = p[(i + 1) % p.length]!; return sum + a.x * b.y - b.x * a.y; }, 0)) / 2;
/** Difference of convex draw polygons. Unknown texture alpha is conservatively treated as covering. */
function uncovered(polygon: Vec2[], cover: Vec2[]): Vec2[][] {
  if (polygonArea(cover) <= 0) return [polygon];
  const signed = cover.reduce((sum, a, i) => { const b = cover[(i + 1) % cover.length]!; return sum + a.x * b.y - b.x * a.y; }, 0);
  const orientation = signed < 0 ? -1 : 1;
  const result: Vec2[][] = [];
  let remainder = polygon;
  for (let i = 0; i < cover.length && remainder.length >= 3; i++) {
    const a = cover[i]!, b = cover[(i + 1) % cover.length]!;
    const distance = (p: Vec2) => orientation * ((b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x));
    const clip = (inside: boolean): Vec2[] => {
      const output: Vec2[] = [];
      for (let j = 0; j < remainder.length; j++) {
        const p = remainder[j]!, q = remainder[(j + 1) % remainder.length]!, dp = distance(p), dq = distance(q);
        const hasP = inside ? dp >= 0 : dp <= 0, hasQ = inside ? dq >= 0 : dq <= 0;
        if (hasP) output.push(p);
        if (hasP !== hasQ) { const t = dp / (dp - dq); output.push({ x: p.x + (q.x - p.x) * t, y: p.y + (q.y - p.y) * t }); }
      }
      return output;
    };
    const outside = clip(false);
    if (polygonArea(outside) > 1e-8) result.push(outside);
    remainder = clip(true);
  }
  return result;
}
/** Geometry only from current-pass drawn nodes with uploaded textures, never source settings/media pixels. */
export function consumedSources(nodes: ConsumedNode[], canvas: Vec2, ready: (id: string, source: unknown) => string | null, scene: (id: string, source: unknown) => ConsumedNode[] | null): ProgramSourceContribution[] {
  let operations = 0, exceeded = false;
  const walk = (items: ConsumedNode[], depth: number): Contribution[] => {
    if (depth > MAX_DEPTH || items.length > MAX_ITEMS) { exceeded = true; return []; }
    const output: Contribution[] = [];
    const covers: Vec2[][] = [];
    for (const node of [...items].reverse()) {
      if (++operations > MAX_OPERATIONS) { exceeded = true; return []; }
      const item = node.item;
      if (!item.visible || item.opacity <= 0 || !node.drawable || !node.src || (!node.sprite.visible && !node.mesh?.visible)) continue;
      if (!Number.isFinite(item.opacity)) { exceeded = true; return []; }
      let layout: ReturnType<typeof computeItemLayout>;
      try { layout = computeItemLayout(item.transform, { x: node.natW, y: node.natH }); }
      catch { exceeded = true; return []; }
      const { crop } = item.transform;
      const { x: w, y: h } = layout.contentSize;
      const shift = item.type === 'group' ? { x: node.groupMinX, y: node.groupMinY } : { x: 0, y: 0 };
      const matrix = item.type === 'group' ? groupContentMatrix(item.transform, { min: shift, size: { x: node.groupW, y: node.groupH } }) : layout.matrix;
      const start = item.type === 'group' ? { x: shift.x + crop.left, y: shift.y + crop.top } : { x: 0, y: 0 };
      const drawPolygon = [{ x: start.x, y: start.y }, { x: start.x + w, y: start.y }, { x: start.x + w, y: start.y + h }, { x: start.x, y: start.y + h }].map((p) => applyToPoint(matrix, p));
      if (!drawPolygon.every((p) => Number.isFinite(p.x) && Number.isFinite(p.y))) { exceeded = true; return []; }
      const add = (contribution: Contribution): void => {
        let pieces = [contribution.polygon];
        for (const cover of covers) {
          if (pieces.length === 0) break;
          if ((operations += pieces.length) > MAX_OPERATIONS) { exceeded = true; return; }
          pieces = pieces.flatMap((piece) => uncovered(piece, cover));
          if (pieces.length > MAX_FRAGMENTS || pieces.some((piece) => piece.length > MAX_VERTICES || !piece.every((p) => Number.isFinite(p.x) && Number.isFinite(p.y)))) { exceeded = true; return; }
        }
        for (const polygon of pieces) output.push({ ...contribution, polygon });
      };
      if (item.type === 'source') {
        const kind = ready(item.sourceId, node.src);
        if (kind && /^[a-zA-Z0-9._:-]{1,128}$/.test(kind) && polygonArea(drawPolygon) > 0) add({ source_id: item.sourceId, kind, polygon: drawPolygon });
      } else {
        const children = item.type === 'group' ? node.children : scene(item.sceneId, node.src);
        if (!children) { if (polygonArea(drawPolygon) > 0) covers.push(drawPolygon); continue; }
        for (const child of walk(children, depth + 1)) {
          const minX = item.type === 'group' ? node.groupMinX : 0, minY = item.type === 'group' ? node.groupMinY : 0;
          const clipped = clippedPolygon(child.polygon, minX + crop.left, minY + crop.top, node.natW - crop.left - crop.right, node.natH - crop.top - crop.bottom);
          if (polygonArea(clipped) <= 0) continue;
          const matrix = item.type === 'group' ? groupContentMatrix(item.transform, { min: { x: minX, y: minY }, size: { x: node.groupW, y: node.groupH } }) : { ...layout.matrix, tx: layout.matrix.tx - layout.matrix.a * crop.left - layout.matrix.c * crop.top, ty: layout.matrix.ty - layout.matrix.b * crop.left - layout.matrix.d * crop.top };
          add({ ...child, polygon: clipped.map((p) => applyToPoint(matrix, p)) });
        }
      }
      if (polygonArea(drawPolygon) > 0) covers.push(drawPolygon);
    }
    return output;
  };
  const drawn = walk(nodes, 0);
  if (exceeded) return [];
  const contributions = drawn.map((item) => {
    const p = clippedPolygon(item.polygon, 0, 0, canvas.x, canvas.y);
    if (polygonArea(p) <= 0) return null;
    const xs = p.map((v) => v.x), ys = p.map((v) => v.y);
    return { source_id: item.source_id, kind: item.kind, bounds: { x: Math.min(...xs), y: Math.min(...ys), width: Math.max(...xs) - Math.min(...xs), height: Math.max(...ys) - Math.min(...ys) } };
  }).filter((value): value is ProgramSourceContribution => value !== null);
  const unique = new Map<string, ProgramSourceContribution>();
  for (const contribution of contributions) {
    const old = unique.get(contribution.source_id);
    if (!old) { if (unique.size < 8) unique.set(contribution.source_id, contribution); continue; }
    const x = Math.min(old.bounds.x, contribution.bounds.x), y = Math.min(old.bounds.y, contribution.bounds.y);
    old.bounds = { x, y, width: Math.max(old.bounds.x + old.bounds.width, contribution.bounds.x + contribution.bounds.width) - x, height: Math.max(old.bounds.y + old.bounds.height, contribution.bounds.y + contribution.bounds.height) - y };
  }
  return [...unique.values()];
}
